'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { StoreLink } from '@/components/store';
import { COMPARE_PATH, compareHref } from '@/lib/compare-params';
import { fetchCompareCards, type CompareCard } from '@/actions/compare-cards';
import { CompareArrowsIcon } from './compare-arrows-icon';
import { useCompare } from './compare-provider';
import { CompareRemoveButton } from './compare-remove-button';

const STATE_KEY = 'compareTrayState';

/**
 * How much of the tray is showing. One named state per control, rather than
 * relative steps, so every button always lands somewhere predictable: the arrow
 * up goes to `full`, the arrow down to `row`, the cross to `pill`.
 */
type TrayState = 'full' | 'row' | 'pill';

const isTrayState = (value: unknown): value is TrayState =>
  value === 'full' || value === 'row' || value === 'pill';

/**
 * The site-wide compare tray.
 *
 * The header's "Jämför" link is desktop-only, so before this existed a customer
 * on a phone could tick four products and then have no way to open the
 * comparison at all. This follows them across every page instead.
 *
 * It sits at `bottom: var(--bottom-chrome)` rather than flush to the viewport.
 * The product page's sticky add-to-cart bar is also a full-width fixed bar on
 * mobile, and it publishes its own height into that property while it is
 * showing — so the tray rides above it instead of burying the buy button. The
 * two share a CSS custom property rather than React state, so neither has to
 * know the other exists.
 */
export function CompareTray() {
  const t = useTranslations('compare');
  const { skus, clear, hydrated } = useCompare();
  const pathname = usePathname();
  const [state, setState] = useState<TrayState>('full');
  const cards = useCompareCards(skus);

  // Read after mount, like the compare list itself: the server renders this too
  // and starting anywhere but the default would be a hydration mismatch.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STATE_KEY);
      if (isTrayState(stored)) setState(stored);
    } catch {
      // A blocked store costs the preference, not the tray.
    }
  }, []);

  const go = useCallback((next: TrayState) => {
    setState(next);
    try {
      localStorage.setItem(STATE_KEY, next);
    } catch {
      // As above.
    }
  }, []);

  // Ticking a new product opens the tray all the way, from whichever state it
  // was in: adding is the one moment the customer most wants to see what they
  // have collected. Unticking is not such a signal — that is someone tidying up,
  // and reopening the panel at them would be noise.
  //
  // Through `go` rather than `setState`, so the state it opens into is the one
  // it keeps. Left unrecorded, the tray would spring open on the tick and then
  // snap back shut on the next page, which reads as a bug.
  //
  // Gated on `hydrated`, and the first run only takes a baseline: without that,
  // the provider reading the stored list on every page load looks like growth,
  // and the tray would reopen itself on each navigation no matter how many
  // times the customer had put it away.
  const previousCount = useRef<number | null>(null);
  useEffect(() => {
    if (!hydrated) return;
    const before = previousCount.current;
    previousCount.current = skus.length;
    if (before !== null && skus.length > before) go('full');
  }, [hydrated, skus.length, go]);

  if (skus.length === 0) return null;

  // Nothing to offer on the compare page itself, where it would only cover the
  // table. `endsWith` rather than equality because the path may carry a store
  // code prefix ("/sv/jamfor").
  if (pathname === COMPARE_PATH || pathname.endsWith(COMPARE_PATH)) return null;

  const count = skus.length;

  if (state === 'pill') {
    return (
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--bottom-chrome,0px)+var(--consent-height,0px))] z-30 transition-[bottom] duration-300">
        {/* The container's right edge, which is where the panel's right edge is
            too — so collapsing and expanding pivot around the same point.

            Lifted a pill-height clear of the viewport floor, unlike the row and
            full panels below: those are wide bars that read as page chrome, but
            a small round button parked on the bottom edge lands on top of the
            footer's link row and swallows the taps meant for it. */}
        <div className="container mx-auto flex justify-end pb-[calc(4rem+env(safe-area-inset-bottom))]">
          {/* Held 8px inside the content's right edge at every width — a round
              button sitting exactly on the line reads as clipped. */}
          <section aria-label={t('title')} className="pointer-events-auto mr-2">
            {/* Icon rather than the word: collapsed is meant to be the smallest
                the tray ever gets, and a label defeats that. The count carries
                the meaning, and `aria-label` keeps the full wording for anyone
                who cannot see the badge.

                `aria-expanded` without `aria-controls`: the panel is unmounted
                while collapsed, and pointing at an id that is not in the
                document would be worse than saying nothing. */}
            <button
              type="button"
              aria-expanded={false}
              aria-label={t('expandWithCount', { count })}
              onClick={() => go('full')}
              className="relative flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-lg transition-colors hover:border-foreground/40"
            >
              <CompareArrowsIcon className="h-5 w-5 fill-none text-accent-700" />
              <span
                aria-hidden="true"
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1 text-[10.5px] font-bold leading-none text-background"
              >
                {count}
              </span>
            </button>
          </section>
        </div>
      </div>
    );
  }

  return (
    // Two layers: the fixed one spans the viewport so the panel can be placed
    // on the same grid as the page, and the inner `container` gives it the main
    // content's bounds to align to. `pointer-events-none` on the outer layer so
    // the page beside a right-aligned panel stays clickable.
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--bottom-chrome,0px)+var(--consent-height,0px))] z-30 transition-[bottom] duration-300">
      <div className="container mx-auto pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        {/* Capped rather than running the full 1600px of the content container:
            four thumbnails plus the link come to roughly 620px, so at container
            width the panel was mostly empty. `ml-auto` hangs it off the right
            edge of the main content rather than centring it, and `w-full` keeps
            it filling the container on a phone, where 700px never binds. */}
        <section
          aria-label={t('title')}
          className="pointer-events-auto ml-auto w-full max-w-[700px] rounded border border-border bg-card/95 px-3 py-2.5 shadow-lg backdrop-blur sm:mr-2 sm:px-4 sm:py-3"
        >
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="min-w-0 flex-1 truncate font-mono text-[11px] uppercase tracking-[0.12em] text-muted sm:text-[12px]">
              {t('selected', { count })}
            </span>

            {/* Only with the thumbnails showing. Emptying the list is
                destructive and undoable only by re-ticking every product, so it
                belongs where the customer can see what they are about to throw
                away — not on the slim row, where it sat beside the arrows with
                nothing on screen to say what it would take. */}
            {state === 'full' && (
              <button
                type="button"
                onClick={clear}
                className="shrink-0 text-[12.5px] font-semibold text-muted underline-offset-2 transition-colors hover:text-foreground hover:underline"
              >
                {t('clear')}
              </button>
            )}

            {/* Only in the row: with the thumbnails showing, the same link sits
                beside them instead. */}
            {state === 'row' && <ComparisonLink skus={skus} />}

            <span className="flex shrink-0 items-center">
              <IconButton
                label={state === 'full' ? t('minimize') : t('showThumbnails')}
                expanded={state === 'full'}
                onClick={() => go(state === 'full' ? 'row' : 'full')}
              >
                <Chevron direction={state === 'full' ? 'down' : 'up'} />
              </IconButton>
              <IconButton label={t('hide')} onClick={() => go('pill')}>
                <span aria-hidden="true" className="text-[13px] leading-none">
                  ✕
                </span>
              </IconButton>
            </span>
          </div>

          {state === 'full' && (
            <div className="mt-2.5 flex items-center gap-2 border-t border-border/60 pt-2.5 sm:gap-3">
              {/* Scrolls rather than wraps: four cards plus the button do not
                  fit a phone, and a tray that grows to two rows would cover
                  more of the page than it is worth. */}
              <ul className="flex min-w-0 flex-1 gap-2 overflow-x-auto sm:gap-3">
                {cards.map((card) => (
                  <li key={card.sku} className="relative w-[76px] shrink-0 sm:w-[110px]">
                    <StoreLink href={`/${card.urlKey}`} className="group block">
                      <span className="relative flex h-[52px] items-center justify-center overflow-hidden rounded bg-surface-100 sm:h-[76px]">
                        {card.image && (
                          <Image
                            src={card.image.url}
                            alt={card.image.label || card.name}
                            fill
                            sizes="(max-width: 640px) 76px, 110px"
                            className="object-contain p-1 mix-blend-multiply"
                          />
                        )}
                      </span>
                      <span className="mt-1 line-clamp-2 text-[10.5px] leading-tight text-muted transition-colors group-hover:text-foreground sm:mt-1.5 sm:text-[12px]">
                        {card.name}
                      </span>
                    </StoreLink>
                    <CompareRemoveButton
                      sku={card.sku}
                      name={card.name}
                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-[10px] text-muted shadow-sm transition-colors hover:border-foreground/40 hover:text-foreground"
                    />
                  </li>
                ))}
              </ul>

              <ComparisonLink skus={skus} />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * The way through to the comparison itself.
 *
 * A text link rather than a filled button: the tray already sits over whatever
 * the customer is reading, and a solid black block on every page competed with
 * the real calls to action — add to cart above all. The arrow carries the
 * "onwards" that the button's weight used to.
 */
function ComparisonLink({ skus }: { skus: string[] }) {
  const t = useTranslations('compare');

  return (
    <StoreLink
      href={compareHref(skus)}
      className="shrink-0 whitespace-nowrap text-[12.5px] font-semibold text-accent-700 underline-offset-4 transition-colors hover:underline sm:text-[13px]"
    >
      {t('viewComparison')}
      <span aria-hidden="true"> →</span>
    </StoreLink>
  );
}

/**
 * Names and thumbnails for `skus`, fetched once per SKU and kept for as long as
 * the tray is mounted — which is the whole session, since it lives in the root
 * layout and survives client-side navigation. A full page load re-fetches,
 * which is unavoidable and cheap: one request for the whole list.
 *
 * Returns only what has arrived, in the order the customer picked things. The
 * tray is perfectly usable without pictures, so nothing here blocks it.
 */
function useCompareCards(skus: string[]): CompareCard[] {
  const [cards, setCards] = useState<Map<string, CompareCard>>(new Map());
  // SKUs already asked about, so a product the catalogue has nothing for is not
  // re-requested on every change to the list.
  const requested = useRef(new Set<string>());
  const key = skus.join(',');

  useEffect(() => {
    const missing = (key ? key.split(',') : []).filter((sku) => !requested.current.has(sku));
    if (missing.length === 0) return;
    for (const sku of missing) requested.current.add(sku);

    fetchCompareCards(missing).then((fetched) => {
      if (fetched.length === 0) return;
      setCards((current) => {
        const next = new Map(current);
        for (const card of fetched) next.set(card.sku, card);
        return next;
      });
    });

    // Deliberately no cleanup cancelling this request. The effect is keyed on
    // the list, so a cleanup would fire every time the customer ticks anything
    // — discarding a response that is still wanted, for a product still in the
    // list, which `requested` has already marked as asked-for and so will never
    // ask for again. That is a thumbnail missing for good while the count still
    // includes it. Landing a late response is harmless: React 18 ignores a set
    // on an unmounted component, and the product is still in `skus` or it is
    // simply not rendered.
  }, [key]);

  return skus.flatMap((sku) => {
    const card = cards.get(sku);
    return card ? [card] : [];
  });
}

function IconButton({
  label,
  expanded,
  onClick,
  children,
}: {
  label: string;
  expanded?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      onClick={onClick}
      className="rounded-[3px] p-2 text-muted transition-colors hover:bg-card-hover hover:text-foreground"
    >
      {children}
    </button>
  );
}

function Chevron({ direction }: { direction: 'up' | 'down' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className={`h-3.5 w-3.5 ${direction === 'up' ? 'rotate-180' : ''}`}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );
}
